import { arrowFlatPath, arrowGeometry, curveLength, curvePoint, curveTangent, flatPathAt, flatPathField, } from './training-curve.js';
import { DIAGRAM_MULTI_STEP_KINDS, DIAGRAM_PITCH_HEIGHT } from './training.js';
/** Bis zu diesem Abstand gehoeren Spieler, Ball oder Pfeilende zu einem Pfeilanfang. */
export const PLAYBACK_REACH = 8;
/** So weit liegt der Ball beim Dribbling vor dem Spieler (bei Symbolgroesse Normal; sonst mit `symbolScale`). */
export const PLAYBACK_DRIBBLE_OFFSET = 2.8;
/** Standzeit vor dem ersten Schritt, damit die Ausgangsstellung zu sehen ist. */
export const PLAYBACK_LEAD_IN = 0.4;
/** Pause zwischen zwei Schritten. */
export const PLAYBACK_PAUSE = 0.4;
/** Kuerzeste und laengste Dauer einer Bewegung in Sekunden. */
export const PLAYBACK_MIN_DURATION = 0.6;
export const PLAYBACK_MAX_DURATION = 1.6;
/** Lange Wege mit Knickpunkten (Slalom, Umweg) duerfen bis zu dieser Dauer laufen, bevor sie schneller werden. */
export const PLAYBACK_MAX_DURATION_POLYLINE = 3;
/** Der hohe Ball fliegt langsamer und mindestens so lange, dass man die Flughoehe sieht. */
export const PLAYBACK_LOFT_MIN_DURATION = 0.9;
export const PLAYBACK_LOFT_MAX_DURATION = 2;
/**
 * Ein Spieler, der per "Hinwechseln" an einem Pfeilende angekommen ist, steht dort neben dem Empfaenger. Fuer den naechsten Pfeil
 * ab dieser Stelle zaehlt er so, als stuende er um so viele Einheiten weiter weg: Der Empfaenger, der schon dort stand, hat Vorrang.
 */
export const PLAYBACK_FOLLOW_TIE = 2;
/** Geschwindigkeit je Pfeilart in Zeichnungseinheiten pro Sekunde: Pass und Schuss sind schneller als Laufen, der Positionswechsel ein lockerer Lauf, der hohe Ball langsamer als der flache Pass. */
export const PLAYBACK_SPEED = { PASS: 60, SHOT: 90, RUN: 30, DRIBBLE: 22, SWITCH: 26, LOFT: 40 };
/** Abstand zweier Feldpunkte in Zeichnungseinheiten. */
function distance(pitch, a, b) {
    return Math.hypot(b.x - a.x, ((b.y - a.y) * DIAGRAM_PITCH_HEIGHT[pitch]) / 100);
}
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
/** Sanfter An- und Auslauf: 0 bis 1 mit langsamem Beginn und Ende. */
export function playbackEase(progress) {
    const p = clamp(progress, 0, 1);
    return p * p * (3 - 2 * p);
}
/** Pfeile mit Laenge - ein Pfeil ohne Laenge wird nicht gezeichnet und bewegt nichts. Mit Knickpunkten hat auch eine Schleife (Anfang = Ende) Laenge. */
function playableArrows(diagram) {
    const result = [];
    diagram.elements.forEach((element, index) => {
        if (element.type === 'arrow' && (element.from[0] !== element.to[0] || element.from[1] !== element.to[1] || (element.points?.length ?? 0) > 0)) {
            result.push({ index, arrow: element });
        }
    });
    return result;
}
/**
 * Ordnet die Pfeile Schritten zu.
 *
 * - Mit `step`: gleiche Nummer = gleichzeitig, aufsteigend. Pfeile ohne `step`
 *   kommen in Listenreihenfolge ans Ende, je Pfeil ein Schritt.
 * - Ohne `step` bei allen Pfeilen: Ein Pfeil, dessen Anfang am Ende eines anderen
 *   liegt, kommt nach diesem; sonst gilt die Listenreihenfolge. Je Pfeil ein
 *   Schritt. Liegen Pfeile im Kreis (Passdreieck), beginnt der erste der Liste.
 *   Positionswechsel (`SWITCH`) kommen dabei ans Ende - sie folgen der Aktion.
 */
export function diagramSteps(diagram) {
    const arrows = playableArrows(diagram);
    if (arrows.length === 0)
        return [];
    if (arrows.some(({ arrow }) => arrow.step !== undefined)) {
        const groups = new Map();
        const unnumbered = [];
        for (const { index, arrow } of arrows) {
            if (arrow.step === undefined) {
                unnumbered.push(index);
            }
            else {
                const group = groups.get(arrow.step) ?? [];
                group.push(index);
                groups.set(arrow.step, group);
            }
        }
        const steps = [...groups.entries()]
            .sort(([a], [b]) => a - b)
            .map(([declared, indices], position) => ({ number: position + 1, declared, arrows: indices }));
        for (const index of unnumbered)
            steps.push({ number: steps.length + 1, declared: null, arrows: [index] });
        return steps;
    }
    // Positionswechsel passieren nach der Aktion: Sie kommen hinter alle anderen Pfeile.
    const ordered = [
        ...deriveOrder(diagram.pitch, arrows.filter(({ arrow }) => arrow.kind !== 'SWITCH')),
        ...deriveOrder(diagram.pitch, arrows.filter(({ arrow }) => arrow.kind === 'SWITCH')),
    ];
    return ordered.map((index, number) => ({ number: number + 1, declared: null, arrows: [index] }));
}
/**
 * Index (in `steps`) des letzten Schritts, ueber den sich ein Pfeil erstreckt: der eigene `stepIndex`, oder bei Laufweg, Dribbling und
 * Positionswechsel mit `step` und `stepEnd` der letzte Schritt mit eingetragener Nummer bis `stepEnd`.
 */
export function arrowLastStepIndex(steps, stepIndex, arrow) {
    const end = arrow.stepEnd;
    if (end === undefined || arrow.step === undefined || !DIAGRAM_MULTI_STEP_KINDS.includes(arrow.kind))
        return stepIndex;
    let last = stepIndex;
    steps.forEach((entry, index) => {
        if (index > stepIndex && entry.declared !== null && entry.declared <= end)
            last = index;
    });
    return last;
}
/** Reihenfolge ohne `step`: Anschluss am Ende eines anderen Pfeils vor Listenreihenfolge. Liefert Indizes in `diagram.elements`. */
function deriveOrder(pitch, arrows) {
    const follows = (earlier, later) => earlier !== later && distance(pitch, pointOf(arrows[earlier].arrow.to), pointOf(arrows[later].arrow.from)) <= PLAYBACK_REACH;
    const placed = new Set();
    const order = [];
    while (order.length < arrows.length) {
        let pick = -1;
        for (let candidate = 0; candidate < arrows.length && pick < 0; candidate += 1) {
            if (placed.has(candidate))
                continue;
            let waiting = false;
            for (let other = 0; other < arrows.length && !waiting; other += 1) {
                if (!placed.has(other) && follows(other, candidate))
                    waiting = true;
            }
            if (!waiting)
                pick = candidate;
        }
        // Ein Kreis von Pfeilen: der erste noch freie der Liste beginnt.
        if (pick < 0)
            pick = arrows.findIndex((_, candidate) => !placed.has(candidate));
        placed.add(pick);
        order.push(arrows[pick].index);
    }
    return order;
}
function pointOf(value) {
    return { x: value[0], y: value[1] };
}
function moveDuration(pitch, kind, geometry) {
    const raw = curveLength(pitch, geometry) / PLAYBACK_SPEED[kind];
    if (kind === 'LOFT')
        return clamp(raw, PLAYBACK_LOFT_MIN_DURATION, PLAYBACK_LOFT_MAX_DURATION);
    const knicks = (geometry.points?.length ?? 0) > 0;
    return clamp(raw, PLAYBACK_MIN_DURATION, knicks ? PLAYBACK_MAX_DURATION_POLYLINE : PLAYBACK_MAX_DURATION);
}
const plans = new WeakMap();
/** Rechnet den Ablauf einer Skizze durch; das Ergebnis wird je Skizzenobjekt gemerkt. */
export function playbackPlan(diagram) {
    const cached = plans.get(diagram);
    if (cached)
        return cached;
    const plan = buildPlan(diagram);
    plans.set(diagram, plan);
    return plan;
}
function buildPlan(diagram) {
    const { pitch, elements } = diagram;
    const dribbleOffset = PLAYBACK_DRIBBLE_OFFSET * (diagram.symbolScale ?? 1);
    const initial = new Map();
    elements.forEach((element, index) => {
        if (element.type === 'player' || element.type === 'ball')
            initial.set(index, { x: element.x, y: element.y });
    });
    const isBall = new Set([...initial.keys()].filter((index) => elements[index]?.type === 'ball'));
    const state = new Map(initial);
    let nextBall = elements.length;
    const allSteps = diagramSteps(diagram);
    /** Bewegungen je Schritt und die Spannen ueber mehrere Schritte (Index des ersten und letzten Schritts, natuerliche Dauer). */
    const stepMoves = [];
    const spans = [];
    /** Ankunft am Ende eines spaeteren Schritts (Spannen ueber mehrere Schritte): Schrittindex -> Element -> Ziel. */
    const arrivals = new Map();
    /** Elemente, die noch laufen, bis zu welchem Schrittindex: Pfeile der Zwischenschritte finden sie nicht. */
    const busy = new Map();
    /** Spieler, die per "Hinwechseln" an ihr Ziel gelaufen sind und seither nicht wieder selbst gelaufen sind (siehe `PLAYBACK_FOLLOW_TIE`). */
    const latecomers = new Set();
    const nearest = (candidates, from, taken) => {
        let best = null;
        let bestScore = Infinity;
        for (const index of candidates) {
            if (taken.has(index))
                continue;
            const at = state.get(index);
            if (!at)
                continue;
            const gap = distance(pitch, from, at);
            if (gap > PLAYBACK_REACH)
                continue;
            const score = gap + (latecomers.has(index) ? PLAYBACK_FOLLOW_TIE : 0);
            if (best === null || score < bestScore) {
                best = index;
                bestScore = score;
            }
        }
        return best;
    };
    const players = () => [...state.keys()].filter((index) => !isBall.has(index));
    allSteps.forEach((step, stepIndex) => {
        // Alle Pfeile eines Schritts laufen gleichzeitig und sehen die Lage zu Schrittbeginn.
        const taken = new Set();
        for (const [entity, last] of [...busy]) {
            if (last >= stepIndex)
                taken.add(entity);
            else
                busy.delete(entity);
        }
        const arrived = new Map();
        const moves = [];
        for (const arrowIndex of step.arrows) {
            const arrow = elements[arrowIndex];
            const geometry = arrowGeometry(arrow);
            const { from, to, via } = geometry;
            const path = arrowFlatPath(pitch, geometry);
            const duration = moveDuration(pitch, arrow.kind, geometry);
            const move = { arrow: arrowIndex, kind: arrow.kind, from, to, via, points: geometry.points ?? [], smooth: geometry.smooth === true, follow: arrow.kind === 'PASS' && arrow.follow === true, endStep: step.number, duration, player: null, ball: null, tracks: [] };
            /** Ball fuer diesen Pfeil: einer in der Naehe, sonst der einzige vorhandene, sonst ein neuer. */
            const claimBall = (appearAt, target, shape = {}) => {
                const near = nearest(isBall, from, taken);
                if (near !== null) {
                    taken.add(near);
                    move.ball = { index: near, mode: 'near' };
                    move.tracks.push({ entity: near, from: state.get(near), to: target, ...shape });
                    return;
                }
                const only = isBall.size === 1 ? [...isBall][0] : null;
                if (only !== null && !taken.has(only)) {
                    taken.add(only);
                    move.ball = { index: only, mode: 'jump' };
                    move.tracks.push({ entity: only, from: appearAt, to: target, ...shape });
                    return;
                }
                const created = nextBall;
                nextBall += 1;
                isBall.add(created);
                taken.add(created);
                move.ball = { index: created, mode: 'appear' };
                move.tracks.push({ entity: created, from: appearAt, to: target, appears: true, ...shape });
            };
            if (arrow.kind === 'PASS' || arrow.kind === 'SHOT' || arrow.kind === 'LOFT') {
                // Hinwechseln: der Ball fliegt mit Pass-Tempo voraus, der Passgeber (Spieler am Pfeilanfang) laeuft ihm mit dem Tempo des Positionswechsels nach.
                const follower = move.follow ? nearest(players(), from, taken) : null;
                claimBall(from, to, { ...(via ? { via } : {}), ...(path ? { path } : {}), ...(arrow.kind === 'LOFT' ? { lofted: true } : {}), ...(follower !== null ? { duration } : {}) });
                if (follower !== null) {
                    taken.add(follower);
                    latecomers.add(follower);
                    move.player = follower;
                    const runDuration = moveDuration(pitch, 'SWITCH', geometry);
                    move.duration = Math.max(duration, runDuration);
                    move.tracks.push({ entity: follower, from: state.get(follower), to, ...(via ? { via } : {}), ...(path ? { path } : {}), duration: runDuration });
                }
            }
            else {
                const runner = nearest(players(), from, taken);
                if (runner !== null) {
                    taken.add(runner);
                    latecomers.delete(runner);
                    move.player = runner;
                    const start = state.get(runner);
                    move.tracks.push({ entity: runner, from: start, to, ...(via ? { via } : {}), ...(path ? { path } : {}) });
                    if (arrow.kind === 'DRIBBLE') {
                        // Der Ball liegt leicht vor dem Spieler, in Laufrichtung (bei Kurven: in Richtung der Tangente).
                        const lead = (point, t) => {
                            const direction = curveTangent(pitch, geometry, t);
                            return {
                                x: clamp(point.x + direction.x * dribbleOffset, 0, 100),
                                y: clamp(point.y + ((direction.y * dribbleOffset) / DIAGRAM_PITCH_HEIGHT[pitch]) * 100, 0, 100),
                            };
                        };
                        claimBall(lead(start, 0), lead(to, 1), path
                            ? { lead: { distance: dribbleOffset, curve: geometry, path } }
                            : via
                                ? { lead: { distance: dribbleOffset, curve: { from: start, to, via } } }
                                : {});
                    }
                }
            }
            // Laeuft bis Schritt: Spieler (und Dribbel-Ball) kommen erst am Ende des letzten Schritts an und sind bis dahin belegt.
            const lastIndex = arrowLastStepIndex(allSteps, stepIndex, arrow);
            if (lastIndex > stepIndex && move.tracks.length > 0) {
                move.endStep = allSteps[lastIndex].number;
                spans.push({ move, from: stepIndex, to: lastIndex, natural: Math.max(curveLength(pitch, geometry) / PLAYBACK_SPEED[arrow.kind], PLAYBACK_MIN_DURATION) });
                const later = arrivals.get(lastIndex) ?? new Map();
                for (const track of move.tracks) {
                    later.set(track.entity, track.to);
                    busy.set(track.entity, lastIndex);
                }
                arrivals.set(lastIndex, later);
            }
            moves.push(move);
        }
        for (const move of moves) {
            if (move.endStep !== step.number)
                continue;
            for (const track of move.tracks)
                arrived.set(track.entity, track.to);
        }
        for (const [entity, at] of arrived)
            state.set(entity, at);
        for (const [entity, at] of arrivals.get(stepIndex) ?? [])
            state.set(entity, at);
        stepMoves.push(moves);
    });
    // Zeitachse: Dauer je Schritt = die laengste einzelne Bewegung; ein Schritt ohne eigene Bewegung, durch den ein Lauf nur hindurchgeht, hat die Mindestdauer.
    const lengths = allSteps.map((step, index) => Math.max(0, ...stepMoves[index].filter((move) => move.endStep === step.number).map((move) => move.duration)));
    for (const { from, to } of spans) {
        for (let index = from; index <= to; index += 1)
            lengths[index] = Math.max(lengths[index], PLAYBACK_MIN_DURATION);
    }
    // Ist der Weg allein laenger als die ueberspannten Schritte samt Pausen, werden diese anteilig laenger (Gewicht = bisherige Dauer);
    // Laengen wachsen nur, eine schon erfuellte Spanne bleibt also erfuellt.
    for (const { from, to, natural } of spans) {
        let total = (to - from) * PLAYBACK_PAUSE;
        let weight = 0;
        for (let index = from; index <= to; index += 1) {
            total += lengths[index];
            weight += lengths[index];
        }
        if (total >= natural)
            continue;
        const deficit = natural - total;
        for (let index = from; index <= to; index += 1)
            lengths[index] = lengths[index] + (deficit * lengths[index]) / weight;
    }
    const plannedSteps = [];
    let clock = PLAYBACK_LEAD_IN;
    allSteps.forEach((step, index) => {
        plannedSteps.push({ ...step, moves: stepMoves[index], start: clock, end: clock + lengths[index] });
        clock += lengths[index] + PLAYBACK_PAUSE;
    });
    // Eine Bewegung ueber mehrere Schritte dauert vom Beginn des ersten bis zum Ende des letzten.
    for (const { move, from, to } of spans)
        move.duration = plannedSteps[to].end - plannedSteps[from].start;
    const last = plannedSteps[plannedSteps.length - 1];
    return { steps: plannedSteps, duration: last ? last.end : 0, initial };
}
/** Beginn eines Schritts in Sekunden; fuer 0 und kleiner 0, fuer zu grosse Nummern die Gesamtdauer. */
export function playbackStepStart(diagram, step) {
    const plan = playbackPlan(diagram);
    if (step <= 0)
        return 0;
    return plan.steps[step - 1]?.start ?? plan.duration;
}
/** Ende eines Schritts in Sekunden (die Stellung nach dem Schritt); Schritt 0 ist die Ausgangsstellung. */
export function playbackStepEnd(diagram, step) {
    const plan = playbackPlan(diagram);
    if (step <= 0)
        return 0;
    return plan.steps[step - 1]?.end ?? plan.duration;
}
/**
 * Ort eines bewegten Elements: gerade zwischen `from` und `to`, bei `via` auf der
 * Bezierkurve (Parameter t = Fortschritt nach Anlauf und Auslauf). Am Ende genau `to`.
 */
function trackPosition(pitch, track, progress, eased) {
    if (progress >= 1)
        return track.to;
    if (track.lead) {
        // Ball des gebogenen Dribblings: vor dem Spieler in Richtung der Tangente. Liegt er zu Beginn
        // nicht genau dort, schliesst sich die Luecke bis zum Ende des Wegs.
        const { distance: ahead, curve, path } = track.lead;
        const scale = DIAGRAM_PITCH_HEIGHT[pitch];
        const lead = (t) => {
            const at = path ? flatPathField(pitch, path, t) : curvePoint(curve, t);
            const direction = path ? flatPathDirection(path, t) : curveTangent(pitch, curve, t);
            return { x: at.x + direction.x * ahead, y: at.y + ((direction.y * ahead) / scale) * 100 };
        };
        const now = lead(eased);
        const start = lead(0);
        return {
            x: clamp(now.x + (track.from.x - start.x) * (1 - eased), 0, 100),
            y: clamp(now.y + (track.from.y - start.y) * (1 - eased), 0, 100),
        };
    }
    if (track.path) {
        // Linienzug: nach Weglaenge. Ein Element, das nicht genau am Anfang liegt, rueckt im Lauf auf den Weg.
        const at = flatPathField(pitch, track.path, eased);
        const start = flatPathField(pitch, track.path, 0);
        return { x: at.x + (track.from.x - start.x) * (1 - eased), y: at.y + (track.from.y - start.y) * (1 - eased) };
    }
    if (track.via)
        return curvePoint({ from: track.from, to: track.to, via: track.via }, eased);
    return { x: track.from.x + (track.to.x - track.from.x) * eased, y: track.from.y + (track.to.y - track.from.y) * eased };
}
/** Laufrichtung (Einheitsvektor in Zeichnungseinheiten) eines Weges beim Anteil `fraction`. */
function flatPathDirection(path, fraction) {
    const at = flatPathAt(path, fraction * path.length);
    return { x: at.ux, y: at.uy };
}
/**
 * Laufrichtung eines Spielers auf seinem Weg bei `eased` in Grad (0 = nach oben, im Uhrzeigersinn, 0 bis 359);
 * `null`, wenn der Weg an dieser Stelle keine Richtung hat.
 */
function trackHeading(pitch, track, eased) {
    let direction;
    if (track.path) {
        direction = flatPathDirection(track.path, eased);
    }
    else if (track.via) {
        direction = curveTangent(pitch, { from: track.from, to: track.to, via: track.via }, eased);
    }
    else {
        direction = { x: track.to.x - track.from.x, y: ((track.to.y - track.from.y) * DIAGRAM_PITCH_HEIGHT[pitch]) / 100 };
    }
    if (Math.hypot(direction.x, direction.y) < 1e-9)
        return null;
    // y waechst nach unten: Richtung "oben" ist (0, -1) und bekommt 0 Grad.
    const degrees = (Math.atan2(direction.x, -direction.y) * 180) / Math.PI;
    return Math.round(((degrees % 360) + 360) % 360) % 360;
}
/** Stand der Skizze zum Zeitpunkt `time` in Sekunden. Nach dem letzten Schritt bleibt der Endzustand stehen. */
export function playbackFrame(diagram, time) {
    const plan = playbackPlan(diagram);
    const at = Number.isFinite(time) ? clamp(time, 0, plan.duration) : 0;
    const positions = new Map(plan.initial);
    const facing = new Map();
    diagram.elements.forEach((element, index) => {
        if (element.type === 'player' && element.facing !== undefined)
            facing.set(index, element.facing);
    });
    const lift = new Map();
    const activeArrows = [];
    const doneArrows = [];
    let started = 0;
    for (const step of plan.steps) {
        if (at < step.start)
            break;
        started += 1;
        for (const move of step.moves) {
            for (const track of move.tracks) {
                // Jedes Element hat seine Dauer: beim Hinwechseln fliegt der Ball schneller, als der Passgeber laeuft.
                const progress = clamp((at - step.start) / (track.duration ?? move.duration), 0, 1);
                const eased = playbackEase(progress);
                positions.set(track.entity, trackPosition(diagram.pitch, track, progress, eased));
                if (track.lofted && progress > 0 && progress < 1)
                    lift.set(track.entity, Math.sin(Math.PI * eased));
                // Ein laufender Spieler mit Blickrichtung schaut dorthin, wohin er laeuft; danach zurueck auf den gespeicherten Wert.
                if (move.player === track.entity && facing.has(track.entity) && progress > 0 && progress < 1) {
                    const heading = trackHeading(diagram.pitch, track, eased);
                    if (heading !== null)
                        facing.set(track.entity, heading);
                }
            }
            // Eine Bewegung ueber mehrere Schritte ist bis zum Ende ihres letzten Schritts aktiv.
            (at <= (plan.steps[move.endStep - 1]?.end ?? step.end) ? activeArrows : doneArrows).push(move.arrow);
        }
    }
    return {
        time: at,
        duration: plan.duration,
        step: started,
        stepCount: plan.steps.length,
        finished: at >= plan.duration,
        positions,
        facing,
        lift,
        activeArrows,
        doneArrows,
    };
}
/** So viele Strecken hat der abgetastete Weg einer Kurve bei vollem Fortschritt. */
const TRAIL_SEGMENTS = 24;
/**
 * Die Wege, die Spieler und Baelle bis `time` tatsaechlich zurueckgelegt haben - fuer die Darstellung
 * "Spur" statt der Pfeile. Bewegungen, die noch nicht begonnen haben, fehlen; der Ball eines
 * Dribblings laeuft neben dem Spieler und bekommt keine eigene Spur.
 */
export function playbackTrails(diagram, time) {
    const plan = playbackPlan(diagram);
    const at = Number.isFinite(time) ? clamp(time, 0, plan.duration) : 0;
    const trails = [];
    for (const step of plan.steps) {
        if (at < step.start)
            break;
        for (const move of step.moves) {
            for (const track of move.tracks) {
                const progress = clamp((at - step.start) / (track.duration ?? move.duration), 0, 1);
                if (progress <= 0)
                    continue;
                if (move.kind === 'DRIBBLE' && move.ball?.index === track.entity)
                    continue;
                const curved = Boolean(track.via || track.lead || track.path);
                const segments = curved ? Math.max(2, Math.ceil(TRAIL_SEGMENTS * progress)) : 1;
                const points = [];
                for (let index = 0; index <= segments; index += 1) {
                    const p = (progress * index) / segments;
                    points.push(trackPosition(diagram.pitch, track, p, playbackEase(p)));
                }
                trails.push({ arrow: move.arrow, kind: move.kind, entity: track.entity, points, ...(move.follow && move.player === track.entity ? { follow: true } : {}) });
            }
        }
    }
    return trails;
}
//# sourceMappingURL=training-playback.js.map